import {trackEvent} from 'analytics/Analytics';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import FileStates from 'FileStates';
import {
  ExternalFileStructureInfo,
  getCoordinateStructureId,
} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import type {ViewState} from 'model-hub/scientific-models/structure-prediction/structurePredictionViewState';
import {ProteinVersion} from 'molbio/aa-sequences/util/AaSequenceConstants';
import {MolstarViewableExternalFile} from 'molbio/molstar/constants';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';

import AlphaFoldTabState from './AlphaFoldTabState';

interface UnavailableStructureInfo {
  structureFile: null;
  model: null;
  modelVersion: null;
  structureFileOutOfDate: true;
  versionId: null;
  coordinateStructureId: null;
  createdAt: null;
}

const UNAVAILABLE_STRUCTURE_INFO: UnavailableStructureInfo = {
  structureFile: null,
  model: null,
  structureFileOutOfDate: true,
  modelVersion: null,
  versionId: null,
  coordinateStructureId: null,
  createdAt: null,
};

/**
 * Utility to evaluate info about a Protein's AlphaFold structure files.
 *
 * structureFile: the most recent completed structure file available, or undefined if there are none.
 * structureFileOutOfDate: indicates whether structureFile belongs to the most recent protein version.
 */
export function getStructureInfoFromVersions(
  currentlyViewedAaSequenceVersionId: number,
  versions?: Array<ProteinVersion>
): UnavailableStructureInfo | ExternalFileStructureInfo {
  if (versions === undefined || versions.length === 0) {
    return UNAVAILABLE_STRUCTURE_INFO;
  }
  const currentVersion = versions.find((version) => version.id === currentlyViewedAaSequenceVersionId);

  // Return null values if the requested version doesn't exist
  if (!currentVersion) {
    return UNAVAILABLE_STRUCTURE_INFO;
  }

  const currentVersionStructures = currentVersion.structures;
  if (currentVersionStructures && currentVersionStructures.length) {
    const versionId = currentVersion.id;
    // Sort structures by creation date (ISO 8601 strings sort correctly lexicographically)
    // and select the most recent one (last after sorting)
    const sortedStructures = currentVersionStructures
      .slice()
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const structure = sortedStructures[sortedStructures.length - 1];
    return {
      preSigned: false,
      structureFile: structure.externalFile,
      model: structure.model,
      modelVersion: structure.modelVersion,
      structureFileOutOfDate: false,
      versionId,
      coordinateStructureId: structure.id,
      createdAt: structure.createdAt,
      componentIds: structure.componentIds,
    };
  }

  const flattenedVersionStructures = versions.flatMap((version) =>
    version.structures.map((structure) => ({
      structure,
      versionId: version.id,
      model: structure.model,
      modelVersion: structure.modelVersion,
    }))
  );
  if (flattenedVersionStructures.length) {
    // Sort by creation date (ISO 8601 strings sort correctly lexicographically)
    // and select the most recent structure (last after sorting)
    const sortedStructures = flattenedVersionStructures
      .slice()
      .sort((a, b) => a.structure.createdAt.localeCompare(b.structure.createdAt));
    const {versionId, structure, model, modelVersion} = sortedStructures[sortedStructures.length - 1];
    return {
      preSigned: false,
      structureFile: structure.externalFile,
      model,
      modelVersion,
      structureFileOutOfDate: true,
      versionId,
      coordinateStructureId: structure.id,
      createdAt: structure.createdAt,
      componentIds: structure.componentIds,
    };
  }
  return UNAVAILABLE_STRUCTURE_INFO;
}

export async function downloadStructureFile(
  structureFile: MolstarViewableExternalFile,
  proteinId: string,
  versionId: number
): Promise<void> {
  const signedURL = await performSignedFileQuery(structureFile.id, structureFile.url, proteinId, versionId);
  if (signedURL == null) {
    return;
  }
  window.open(signedURL, '_blank', 'noopener noreferrer');
  trackEvent(MOLBIO_ANALYTICS_ACTIONS.DOWNLOADED_ALPHAFOLD_STRUCTURE);
}

export function getStructuresForVersion(
  versions?: Array<ProteinVersion>,
  currentVersionId?: number
): Array<ExternalFileStructureInfo> {
  if (!versions || versions.length === 0) {
    return [];
  }

  // If currentVersionId is provided, only return predictions from that version
  // Otherwise, return predictions from all versions (backward compatibility)
  const versionsToProcess = currentVersionId
    ? versions.filter((version) => version.id === currentVersionId)
    : versions;

  const allCoordinateStructures: Array<ExternalFileStructureInfo> = [];
  const fallbackCurrentVersionId = versions[versions.length - 1].id;

  // Iterate through filtered versions and collect their structures
  versionsToProcess.forEach((version) => {
    version.structures.forEach((structure) => {
      allCoordinateStructures.push({
        preSigned: false,
        structureFile: structure.externalFile,
        model: structure.model,
        modelVersion: structure.modelVersion,
        versionId: version.id,
        coordinateStructureId: structure.id,
        createdAt: structure.createdAt,
        structureFileOutOfDate: version.id !== (currentVersionId || fallbackCurrentVersionId),
        componentIds: structure.componentIds,
      });
    });
  });

  // Sort by creation date in reverse chronological order (newest first)
  return allCoordinateStructures.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function findCoordinateStructureById(
  coordinateStructures: Array<ExternalFileStructureInfo>,
  coordinateStructureId: string
): ExternalFileStructureInfo | null {
  return coordinateStructures.find((p) => getCoordinateStructureId(p) === coordinateStructureId) || null;
}

/**
 * Opens a protein in the workspace and activates the 3D Structure tab.
 * Used when navigating to a protein from Model Hub prediction jobs or
 * after submitting a structure prediction from the form.
 */
export async function navigateToProteinStructureTab(proteinId: string): Promise<void> {
  SidepanelActions.close({autoOpenFile: false});

  const fileState = await FileStates.get().openFolderItem(proteinId, {open: true});
  if (!fileState) {
    return;
  }
  const existingTab = fileState.findTab(AlphaFoldTabState);
  if (existingTab != null) {
    existingTab.set({active: true, initialView: 'COORDINATE_STRUCTURES_LIST' as ViewState});
  }
}
